import { MatchDecision, MatchDecisionResult, ReviewItem } from "@/types/review";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { supabase } from "@/integrations/supabase/client";

/**
 * API client for review items (calls review-api edge function)
 */
export class ReviewApiClient {
  /**
   * Get all review items
   */
  public static async getReviewItems(): Promise<{ items: ReviewItem[] }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "getReviewItems" },
      });

      if (error) {
        console.error("Error fetching review items:", error);
        throw error;
      }

      return data || { items: [] };
    } catch (error) {
      console.error('Exception fetching review items:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Get all approved review items
   */
  public static async getApprovedReviewItems(): Promise<{ items: ReviewItem[] }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "getApprovedReviewItems" },
      });

      if (error) {
        console.error("Error fetching approved review items:", error);
        throw error;
      }

      return data || { items: [] };
    } catch (error) {
      console.error('Exception fetching approved review items:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Get all needs_manual_review items
   */
  public static async getNeedsManualReviewItems(): Promise<{ items: ReviewItem[] }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "getNeedsManualReviewItems" },
      });

      if (error) {
        console.error("Error fetching needs_manual_review items:", error);
        throw error;
      }

      return data || { items: [] };
    } catch (error) {
      console.error('Exception fetching needs_manual_review items:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Idempotently create a needs_manual_review suggestion for a rejected unsupported
   * document (if not already present) and return the ReviewItem ready for the modal.
   */
  public static async prepareManualReview(documentId: string): Promise<{ item: ReviewItem | null }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "prepareManualReview", documentId },
      });

      if (error) {
        console.error("Error preparing manual review:", error);
        throw error;
      }

      return data || { item: null };
    } catch (error) {
      console.error('Exception preparing manual review:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * One suggestion as a ReviewItem, whatever its status. `item` is null when the suggestion
   * does not exist or is not visible to the caller (RLS).
   */
  public static async getReviewItem(suggestionId: string): Promise<{ item: ReviewItem | null }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "getReviewItem", suggestionId },
      });

      if (error) {
        console.error("Error fetching review item:", error);
        throw error;
      }

      return data || { item: null };
    } catch (error) {
      console.error('Exception fetching review item:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Submit a choice/approve review decision
   */
  public static async submitChoice(
    reviewItemId: string,
    selectedOptionId: string
  ): Promise<{ success: boolean }> {
    try {
      console.log('ReviewApiClient: Submitting choice', { reviewItemId, selectedOptionId });

      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: {
          action: "submitReview",
          reviewId: reviewItemId,
          reviewAction: "approve",
          selectedSuggestionId: selectedOptionId
        },
      });

      if (error) {
        console.error("Error submitting choice:", error);
        throw error;
      }

      return data || { success: false };
    } catch (error) {
      console.error('Exception submitting choice:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * FIN-1071: a person's decision on a matcher's Review item, for every matcher. A lost race
   * comes back as a 409 whose message names who resolved or is booking the item.
   */
  public static async decideMatch(suggestionId: string, decision: MatchDecision): Promise<MatchDecisionResult> {
    const { data, error } = await supabase.functions.invoke("review-api", {
      method: "POST",
      body: { action: "decideMatch", suggestionId, decision },
    });
    if (error) throw await toEdgeFunctionFailure(error, "Couldn't save the decision. Please try again.");
    return data as MatchDecisionResult;
  }

  /**
   * Submit manual bookkeeping entries for an unsupported document
   */
  public static async submitManualSuggestion(params: {
    suggestionId: string;
    description: string;
    verificationDate: string;
    vendor?: string;
    journalEntries: Array<{
      accountNumber: string;
      debit?: number | null;
      credit?: number | null;
      description?: string;
    }>;
  }): Promise<{ success: boolean }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "submitManualSuggestion", ...params },
      });

      if (error) {
        console.error("Error submitting manual suggestion:", error);
        throw error;
      }

      return data || { success: false };
    } catch (error) {
      console.error('Exception submitting manual suggestion:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Decline an unsupported document (not a financial document)
   */
  public static async declineUnsupportedDocument(
    suggestionId: string
  ): Promise<{ success: boolean }> {
    try {
      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: { action: "declineUnsupportedDocument", suggestionId },
      });

      if (error) {
        console.error("Error declining unsupported document:", error);
        throw error;
      }

      return data || { success: false };
    } catch (error) {
      console.error('Exception declining unsupported document:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Submit context information
   */
  public static async submitContext(
    reviewItemId: string,
    contextText: string
  ): Promise<{ success: boolean }> {
    try {
      console.log('ReviewApiClient: Submitting context', { reviewItemId, contextText });

      const { data, error } = await supabase.functions.invoke("review-api", {
        method: "POST",
        body: {
          action: "submitReview",
          reviewId: reviewItemId,
          reviewAction: "reject",
          rejectionReason: contextText
        },
      });

      if (error) {
        console.error("Error submitting context:", error);
        throw error;
      }

      return data || { success: false };
    } catch (error) {
      console.error('Exception submitting context:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }
}
